import { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { toast } from 'react-toastify';
import useEmailMarketing from '../../../hooks/useEmailMarketing';

// Settings for the "Angemeldet für Liste" start block: list selection,
// birthday audience sync, list creation, signup method and existing contacts.
const ListSignupSettings = ({ block, lists, readOnly, onConfigChange, onListsChanged }) => {
  const { createList, syncBirthdayAudience } = useEmailMarketing();
  const config = block.config || {};
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [newListDialogOpen, setNewListDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const newListNameRef = useRef(null);

  // Lists sorted alphabetically (no flow is special-cased).
  const sortedLists = useMemo(
    () => [...lists].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [lists]
  );

  const selectedListKnown = !config.listId || lists.some(entry => entry._id === config.listId);

  const handleSync = async () => {
    setBusy(true);
    try {
      const result = await syncBirthdayAudience(config.listId);
      if (result !== null) {
        const added = result?.added ?? result?.addedCount ?? result?.numAdded;
        toast.success(
          typeof added === 'number'
            ? `${added} Kontakte zur Liste hinzugefügt`
            : 'Zielgruppe wurde synchronisiert'
        );
        onListsChanged();
      }
      setSyncDialogOpen(false);
    } catch {
      toast.error('Fehler beim Befüllen der Zielgruppe');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateList = async () => {
    const name = newListNameRef.current?.value?.trim();
    if (!name) {
      toast.error('Bitte einen Namen für die Liste angeben');
      return;
    }
    setBusy(true);
    try {
      const result = await createList({ name });
      if (result !== null) {
        const doc = result?.list || result;
        toast.success('Liste erstellt');
        onListsChanged();
        if (doc?._id) onConfigChange({ listId: doc._id });
      }
      setNewListDialogOpen(false);
    } catch {
      toast.error('Fehler beim Erstellen der Liste');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth size='small'>
        <InputLabel id={`fz-list-select-${block.blockId}`}>E-Mail Liste auswählen</InputLabel>
        <Select
          labelId={`fz-list-select-${block.blockId}`}
          label='E-Mail Liste auswählen'
          value={config.listId || ''}
          disabled={readOnly}
          onChange={event => onConfigChange({ listId: event.target.value })}
        >
          {!selectedListKnown && (
            <MenuItem value={config.listId}>Unbekannte Liste</MenuItem>
          )}
          {sortedLists.map(list => (
            <MenuItem key={list._id} value={list._id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                <span>{list.name}</span>
                <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
                  {list.memberCount ?? 0} Kontakte
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          variant='outlined'
          size='small'
          startIcon={<GroupAddIcon />}
          disabled={readOnly || !config.listId || busy}
          onClick={() => setSyncDialogOpen(true)}
        >
          Zielgruppe automatisch füllen
        </Button>
        <Button
          variant='text'
          size='small'
          startIcon={<PlaylistAddIcon />}
          disabled={readOnly || busy}
          onClick={() => setNewListDialogOpen(true)}
        >
          Neue Liste
        </Button>
      </Box>
      <FormControl fullWidth size='small'>
        <InputLabel id={`fz-signup-method-${block.blockId}`}>Anmeldemethode</InputLabel>
        <Select
          labelId={`fz-signup-method-${block.blockId}`}
          label='Anmeldemethode'
          value={config.signupMethod || 'any'}
          disabled={readOnly}
          onChange={event => onConfigChange({ signupMethod: event.target.value })}
        >
          <MenuItem value='any'>Beliebige Methode</MenuItem>
        </Select>
      </FormControl>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(config.includeExistingContacts)}
              disabled={readOnly}
              onChange={event => onConfigChange({ includeExistingContacts: event.target.checked })}
            />
          }
          label='Bestehende Kontakte einbeziehen'
        />
        <Typography variant='caption' color='warning.main' sx={{ display: 'block' }}>
          Wenn aktiviert, laufen alle bestehenden Kontakte der Liste sofort in den Flow.
        </Typography>
      </Box>

      <Dialog open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)} maxWidth='xs'>
        <DialogTitle>Zielgruppe automatisch füllen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Alle Kontakte mit hinterlegtem Geburtsdatum und Marketing-Einwilligung (Opt-in) werden
            der ausgewählten Liste hinzugefügt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialogOpen(false)}>Abbrechen</Button>
          <Button variant='contained' onClick={handleSync} disabled={busy}>
            Füllen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newListDialogOpen} onClose={() => setNewListDialogOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Neue Liste erstellen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size='small'
            margin='dense'
            label='Name der Liste'
            inputRef={newListNameRef}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewListDialogOpen(false)}>Abbrechen</Button>
          <Button variant='contained' onClick={handleCreateList} disabled={busy}>
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListSignupSettings;
